import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Link2 } from "lucide-react";
import api from "../../lib/api";
import { Label } from "../../components/common";
import { Panel, Distribution, SsiTile, Recovery, Mini, fmtV, weekLabel, PdfButton } from "../../components/scorecard";
import PeriodFilter from "../../components/PeriodFilter";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import ResponsesSummary from "../../components/ResponsesSummary";
import { feedbackHref } from "../../lib/feedbackLink";

const COLORS = ["#4338CA", "#10B981", "#F59E0B", "#EF4444", "#0EA5E9", "#8B5CF6", "#EC4899", "#64748B"];

function MultiTrend({ groups, weeks, testId }) {
  if (!groups.length) return <p className="py-8 text-center text-sm text-slate-400">No data yet.</p>;
  const data = weeks.map((w, i) => Object.fromEntries([["label", weekLabel(w)], ...groups.map((g) => [g.name, g.series[i]?.ssi])]));
  return (
    <div style={{ width: "100%", height: 260 }} data-testid={testId}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94A3B8" }} minTickGap={20} />
          <YAxis domain={[0, 5]} tick={{ fontSize: 10, fill: "#94A3B8" }} width={30} />
          <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {groups.slice(0, 8).map((g, i) => <Line key={g.key} dataKey={g.name} stroke={COLORS[i]} strokeWidth={2} dot={false} connectNulls />)}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function Heatmap({ rows, labels, onPick }) {
  const keys = Object.keys(labels);
  const max = Math.max(1, ...rows.flatMap((r) => keys.map((k) => r.counts[k])));
  if (!rows.length) return <p className="text-sm text-slate-400">No comment themes yet.</p>;
  return (
    <div className="overflow-x-auto sv-scroll">
      <table className="w-full min-w-[720px] text-xs" data-testid="exec-heatmap">
        <thead><tr className="text-left text-slate-400"><th className="py-1.5">Outlet</th>{keys.map((k) => <th key={k} className="px-1 text-center font-semibold">{labels[k]}</th>)}</tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.location_id}>
            <td className="py-1 pr-2 font-semibold text-slate-700"><button className="hover:text-indigo-700 hover:underline" onClick={() => onPick({ location: r.location_id })}>{r.location}</button></td>
            {keys.map((k) => {
              const n = r.counts[k];
              return <td key={k} className="p-0.5"><button disabled={!n} onClick={() => onPick({ location: r.location_id, theme: k })} data-testid={`heat-${r.location_id}-${k}`} className="w-full rounded py-1.5 text-center font-semibold transition-opacity hover:opacity-80 disabled:cursor-default" style={{ background: n ? `rgba(67,56,202,${0.12 + 0.75 * (n / max)})` : "#F8FAFC", color: n / max > 0.5 ? "#fff" : "#334155" }}>{n}</button></td>;
            })}
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

export default function ExecutiveScorecard() {
  const [days, setDays] = useState("7");
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  const go = (extra) => navigate(feedbackHref({ days, ...extra }));
  useEffect(() => {
    setD(null);
    api.get(`/scorecards/executive?days=${days}`).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [days]);

  return (
    <div className="space-y-6" data-testid="executive-scorecard-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><Label>Leadership</Label><h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Executive Scorecard</h1></div>
        <div className="flex flex-wrap gap-2">
          <PeriodFilter value={days} onChange={setDays} testId="exec-period" />
          <PdfButton path={`/scorecards/executive.pdf?days=${days}`} filename={`servantage_executive_scorecard_${days}d.pdf`} testId="exec-pdf-btn" />
        </div>
      </div>
      {!d ? <p className="py-16 text-center text-sm text-slate-400">Loading…</p> : d.error ? <p className="py-16 text-center text-sm text-slate-400">Could not load the scorecard.</p> : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <SsiTile d={d} testId="exec-ssi" sub={`${d.ratings} ratings`} />
            <Mini label="Participation coverage" value={fmtV(d.participation.coverage, "%")} sub={`${d.participation.rated_employees} of ${d.participation.active_employees} active employees rated`} />
            <Mini label="Ratings per active employee" value={fmtV(d.participation.ratings_per_active_employee)} />
            <Mini label="Positive-moment conversion" value={fmtV(d.positive_moments.conversion_rate, "%")}
              sub={`${d.positive_moments.review_clicks} Google review clicks · ${d.positive_moments.commendations} commendations`} />
          </div>
          <Panel title="Service Index trend (weekly)" testId="exec-trends">
            <Tabs defaultValue="outlet">
              <TabsList><TabsTrigger value="outlet" data-testid="exec-tab-outlet">By outlet</TabsTrigger><TabsTrigger value="region" data-testid="exec-tab-region">By region</TabsTrigger><TabsTrigger value="cohort" data-testid="exec-tab-cohort">By tenure cohort</TabsTrigger></TabsList>
              <TabsContent value="outlet"><MultiTrend groups={d.by_outlet} weeks={d.weeks} testId="exec-trend-outlet" /></TabsContent>
              <TabsContent value="region"><MultiTrend groups={d.by_region} weeks={d.weeks} testId="exec-trend-region" /></TabsContent>
              <TabsContent value="cohort"><MultiTrend groups={d.by_cohort} weeks={d.weeks} testId="exec-trend-cohort" /></TabsContent>
            </Tabs>
          </Panel>
          <div className="grid gap-5 lg:grid-cols-2">
            <Panel title="Recovery cases: volume, ageing & closure"><Recovery r={d.recovery} testId="exec-recovery" /></Panel>
            <Panel title="Rating distribution"><Distribution dist={d.distribution} testId="exec-dist" onPick={(s) => go({ rating: s })} />
              <p className="mt-3 text-xs text-slate-400">% of interactions generating feedback needs transaction counts from your POS.</p>
            </Panel>
          </div>
          <Panel title="Theme heatmap (comment mentions by outlet)" testId="exec-heatmap-panel"><Heatmap rows={d.heatmap} labels={d.theme_labels} onPick={({ location, theme }) => (theme ? go({ location_id: location, theme }) : navigate(`/app/store?location=${location}`))} /></Panel>
          <ResponsesSummary days={days} testId="exec-responses-summary" />
          <div className="flex items-start gap-3 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5" data-testid="exec-correlation">
            <Link2 className="mt-0.5 h-5 w-5 text-slate-400" />
            <div><p className="font-semibold text-slate-700">Correlation views</p><p className="text-sm text-slate-500">{d.correlation.message}</p></div>
          </div>
        </>
      )}
    </div>
  );
}
